// /packs endpoints (plan §4, owner only): upload a pack file, read its dry-run diff, apply it.
// The upload is multipart, which the JSON helper in http.ts does not send, so it has its own small
// sender here with the same rules: cookie mode, a checked path, typed errors, a parsed answer.
import type { z } from 'zod';
import { ApiError, NetworkError, UNEXPECTED_RESPONSE } from './errors.ts';
import { idempotencyKey as checkedKey } from './headers.ts';
import {
  API_BASE_PATH,
  apiErrorFrom,
  createHttp,
  type FetchLike,
  type HttpOptions,
} from './http.ts';
import { assertSafePath, pathSegment } from './paths.ts';
import {
  applyResultSchema,
  packDiffEnvelopeSchema,
  type ApplyResult,
  type PackDiff,
} from './schemas/packs.ts';

export interface PacksApi {
  /** POST /packs (multipart field "file"): validates and stores the pack, returns its diff. */
  upload(file: Blob, opts?: { readonly idempotencyKey?: string }): Promise<PackDiff>;
  /** GET /packs/:version/diff: the dry run against live content and people's state. */
  diff(version: string, signal?: AbortSignal): Promise<PackDiff>;
  /** POST /packs/:version/apply: applies in one transaction; 409 lists blockers if any. */
  apply(version: string, opts?: { readonly idempotencyKey?: string }): Promise<ApplyResult>;
}

const UPLOAD_FILE_NAME = 'pack.tgz';
const UNEXPECTED_MESSAGE = 'The portal sent an answer this page did not expect. Reload the page.';

function isAbort(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

async function sendForm<T>(
  options: HttpOptions,
  req: { readonly path: string; readonly form: FormData; readonly key: string | undefined },
  schema: z.ZodType<T>,
): Promise<T> {
  const base = options.baseUrl ?? API_BASE_PATH;
  const doFetch: FetchLike = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  // No Content-Type: the browser writes the multipart boundary itself.
  const headers = new Headers({ Accept: 'application/json' });
  if (req.key !== undefined) headers.set('Idempotency-Key', checkedKey(req.key));
  let res: Response;
  try {
    res = await doFetch(`${base}${assertSafePath(req.path)}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: req.form,
    });
  } catch (err) {
    if (isAbort(err)) throw err;
    throw new NetworkError(err);
  }
  if (!res.ok) throw await apiErrorFrom(res);
  const parsed = schema.safeParse(await res.json().catch(() => undefined));
  if (!parsed.success) throw new ApiError(res.status, UNEXPECTED_RESPONSE, UNEXPECTED_MESSAGE);
  return parsed.data;
}

export function createPacksApi(options: HttpOptions = {}): PacksApi {
  const http = createHttp(options);
  const packPath = (version: string, tail: string): string =>
    `/packs/${pathSegment(version)}/${tail}`;
  return {
    async upload(file, opts = {}) {
      const form = new FormData();
      form.append('file', file, file instanceof File ? file.name : UPLOAD_FILE_NAME);
      const req = { path: '/packs', form, key: opts.idempotencyKey };
      return (await sendForm(options, req, packDiffEnvelopeSchema)).diff;
    },
    diff: async (version, signal) =>
      (
        await http.json(
          { method: 'GET', path: packPath(version, 'diff'), signal },
          packDiffEnvelopeSchema,
        )
      ).diff,
    apply: async (version, opts = {}) =>
      http.json(
        {
          method: 'POST',
          path: packPath(version, 'apply'),
          ...(opts.idempotencyKey === undefined ? {} : { idempotencyKey: opts.idempotencyKey }),
        },
        applyResultSchema,
      ),
  };
}

export * from './schemas/packs.ts';
